/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { deleteItemByIdCfn } from '../shared.functions';
import { getOrderedChildrenCfn } from './functions/get-ordered-children.cfn';
import { groupAppliancesCfn } from './functions/group-appliances.cfn';
import { Island } from './island.class';
import { islandsData } from './islands.data';
import { IslandType } from './enums';
import { Publish } from '../../decorators';
import { updateIslandPositionsCfn } from './functions';
import { Vertex3 } from '../../spatial-classes';

import type { Size } from '../../types';

import { CornerSize, GroupAppliances, IslandsDataIn, IslandsDataOut } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { MOK_ISLAND_DIMENSIONS_BY_TYPE, MOK_SURROUND_PREFIX } from '../surround-material/constants';

const applyMokDimensionsCfn = (islands: Islands): void => {
	for (const island of islands.children) {
		const dims = MOK_ISLAND_DIMENSIONS_BY_TYPE[island.type as string];
		if (dims && island.type !== IslandType.Corner) {
			island.size = { height: dims.height, width: island.size.width, depth: dims.depth };
		}
	}
};

export class Islands extends KitchenDesignBase<IslandsDataIn> {
	#defaultIslandId: string;
	#tempOrigin: Vertex3;

	readonly children: Array<Island>;

	/**
	 * The constructor of the 'Islands' class
	 * @param options provides context, also used to hydrate the instance
	 */
	constructor (options: KitchenDesignOptions<IslandsDataIn> = {}) {
		super(BlueprintInstanceType.Islands, options);

		const { dataIn = null } = options;
		const data = islandsData(dataIn)
		this.children = [];

		this.#defaultIslandId = data.defaultIslandId;
		this.#tempOrigin = null;

		if (data.children.length > 0) {
			data.children.forEach((n) => this.children.push(new Island({ dataIn: n, parent: this })));
		}

		this.setInitialized();

		this.refMap.kitchen?.subscribe('surround_material', 'updated', (event) => {
			const surroundKey = event.payload?.ref?.kitchen?.material?.surround?.key ?? '';
			if (surroundKey.startsWith(MOK_SURROUND_PREFIX)) {
				applyMokDimensionsCfn(this);
			} else {
				this.updateIslandPositions();
			}
		});

		const initialSurroundKey = this.refMap.kitchen?.material?.surround?.key ?? '';
		if (initialSurroundKey.startsWith(MOK_SURROUND_PREFIX)) {
			applyMokDimensionsCfn(this);
		}
	}

	/**
	 *
	 * @param orientation axis.y value of the islands
	 * @returns array of islands mathcing the orientation (angle) parameter
	 */
	public getIslandsByOrientation (orientation: number): Array<Island> {
		return this.children.filter((island) => island.axis.y === orientation);
	}

	/**
	 * A getter that returns the island id of the default island
	 */
	get defaultIslandId (): string { return this.#defaultIslandId }

	/**
	 * a getter that returns the number of islands
	 */
	get length (): number { return this.children.length }

	/**
	 * A getter that represents the centerpoint of default island, which is the origin of islands
	 */
	get origin (): Vertex3 {
		if (this.#tempOrigin === null) this.#tempOrigin = this.getDefaultIsland().origin as Vertex3;
		return this.#tempOrigin;
	}

	/**
	 * Delete an island
	 * @param id id of island to delete
	 */
	@Publish('islands', 'deleted')
	public delete (id: string): void {
		const island = this.findById(id);
		if (!island || island.id === this.#defaultIslandId || island.islandConnectionCount > 1) return;

		// Attached obstructions (e.g. walls) auto-delete with the island — they are not
		// island-to-island joins and never block deletion. Obstructions.delete() also clears
		// the paired target-side connection on this island via deleteConnectionCfn.
		const { obstructions } = this.refMap;
		const attachedObstructionIds = island.connections.children
			.filter((c) => c.targetIsland?.instanceOf === BlueprintInstanceType.Obstruction)
			.map((c) => c.targetIslandId);
		for (const obstructionId of attachedObstructionIds) obstructions?.delete(obstructionId);

		// Cascade the remaining island-to-island connection (a leaf has at most one).
		while (island.connections.children.length > 0) {
			const [c] = island.connections.children;
			if (!island.connections.deleteConnection(c.sourcePanelId, c.targetIslandId)) break;
		}

		deleteItemByIdCfn(this.children, id);
	}

	/**
	 * A method to group the appliance from all islands into a reportable structure
	 *
	 * @returns GroupAppliances
	 */
	public groupAppliances (): GroupAppliances {
		return groupAppliancesCfn(this as Islands);
	}

	/**
	 * A method for setting the default island.
	 *
	 * @param defaultIslandId The id of the island representing the new default. The island must alrweady exist under islands.
	 * @returns true if the island exist and is set as the new default island
	 */
	@Publish('island_default', 'updated')
	public setDefaultIsland (defaultIslandId: string): boolean {
		const island = this.children.find((i) => i.id === defaultIslandId) || null;
		if (island) this.#defaultIslandId = island.id;
		return island !== null;
	}

	/**
	 * A method used to return an instance of the default Island
	 *
	 * @returns instance of Island representing the default island or NULL if default isn't set
	 * */
	public getDefaultIsland (): Island | null {
		return this.children.find((i) => i.id === this.#defaultIslandId) || null;
	}

	/**
	 * Creates and adds a new island to the islands instance
	 * @param islandType IslandsType enum
	 * @param islandSize default base size of island
	 * @param sku string representing the default sku
	 * @returns Instance of island created
	 */
	public newIsland (islandType: IslandType, islandSize: Size | CornerSize): Island {
		const island = new Island({
			dataIn: {
				type: islandType,
				size: islandSize
			},
			parent: this
		});
		this.children.push(island);

		// make this the default island if one doesn't exist
		if (this.#defaultIslandId === '') {
			this.setDefaultIsland(island.id);
		}

		return island;
	}

	/**
	 * method to allows the consumer to call find insead of having to used children.find
	 * @param callback signature matched array.find()
	 * @returns instance if Island or null
	 */
	public find (callback: (key: Island) => boolean | undefined): Island | null {
		return (this.children.find(callback) as Island) || null;
	}

	/**
	 * finds an instance of island using the islands id
	 * @param id island id representing the island we are looking for
	 * @returns instance of the island found, or NULL if not found
	 */
	public findById (id: string): Island {
		return this.children.find((i) => i.id === id);
	}

	/**
	 * finds an instance of island containing a panel matching the provided panel id
	 * @param id island id representing the panel we are looking for
	 * @returns instance of the island found, or NULL if not found
	 */
	public findByPanelId (id: string): Island {
		return this.children.find((i) => i.panels.findById(id) !== null);
	}

	/**
	 * islands origin is based on the default island. Set a temporary value to be read when published.
	 * @param point new origin point for islands
	 * @emits islands_origin.updated
	 */
	@Publish('islands_origin', 'updated')
	// eslint-disable-next-line class-methods-use-this
	public setOrigin (point: Vertex3): void {
		// console.log('Islands.setOrigin(origin)', point);
		this.#tempOrigin = point;
	}

	/**
	 * Update the position of the islands
	 * @emits islands_positions.updated
	 */
	@Publish('islands_positions', 'updated')
	public updateIslandPositions (): void {
		updateIslandPositionsCfn(this as Islands);
	}

	/**
	 * Returns islands in canonical spatial order for estimate/quote display.
	 * Front-row islands are ordered by connection chain traversal starting from the
	 * leftmost island (lowest origin.x). Galley islands (axis.y === 180) are placed
	 * at the end, sorted by the X-position of the front-row island they connect to.
	 * @returns Array of islands in natural left-to-right spatial order
	 */
	get orderedChildren (): Array<Island> { return getOrderedChildrenCfn(this as Islands); }

	public sortByHeight (): Array<Island> { return (this.children as Array<Island>).sort(Islands.sortIslandsByHeight) }
	public sortByLeftView (): Array<Island> { return (this.children as Array<Island>).sort(Islands.sortIslandsByLeftView) }
	public sortByRightView (): Array<Island> { return (this.children as Array<Island>).sort(Islands.sortIslandsByRightView) }

	/**
	 * @returns serialized data representing class state for persistence
	 */
	public serialize (): IslandsDataOut {
		return {
			defaultIslandId: this.#defaultIslandId,
			children: this.children.map((island: Island) => island.serialize())
		};
	}

	// ----------------- STATIC METHODS -----------------
	static sortIslandsByHeight (a: Island, b: Island): number { return a.size.height - b.size.height }
	static sortIslandsByLeftView (a: Island, b: Island): number { return b.origin.x - a.origin.x }
	static sortIslandsByRightView (a: Island, b: Island): number { return a.origin.x - b.origin.x }
}

